import { useParams, useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import { FileWarning } from 'lucide-react';
import { useCase } from '../hooks/useCases';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Button, Card, EmptyState, Skeleton, SkeletonTable } from '../components/ui';
import CaseHeader from '../components/CaseHeader';
import CaseSettings from '../components/CaseSettings';
import DiscoverySection from './case-sections/DiscoverySection';
import InvestigationSection from './case-sections/InvestigationSection';
import EstateSection from './case-sections/EstateSection';
import DraftsSection from './case-sections/DraftsSection';
import AssistantSection from './case-sections/AssistantSection';

export default function CaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: caseData, isLoading, error } = useCase(id);

  useDocumentTitle(caseData?.name);

  if (isLoading) {
    return (
      <div className="flex flex-col h-full min-h-0">
        <div
          data-surface="chrome"
          className="bg-chrome-bg h-16 flex-shrink-0 border-b border-chrome-sep"
        />
        <div className="flex-1 min-h-0 bg-cream-light p-6 space-y-4">
          <Skeleton variant="rect" width="16rem" height={22} />
          <SkeletonTable rows={8} columns={4} />
        </div>
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Card className="w-full max-w-md">
          <EmptyState
            icon={FileWarning}
            title="Failed to load case"
            description="The case may not exist or the server is unavailable."
            action={
              <Button variant="secondary" size="lg" onClick={() => navigate('/')}>
                Back to cases
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <CaseHeader
        caseName={caseData.name}
        clientName={caseData.client_name}
      />

      <div className="flex-1 flex flex-col min-h-0 bg-cream-light">
        <Routes>
          <Route
            path="settings"
            element={<CaseSettings caseData={caseData} onArchived={() => navigate('/')} />}
          />
          <Route path="discovery/*" element={<DiscoverySection caseData={caseData} />} />
          <Route path="investigation/*" element={<InvestigationSection caseData={caseData} />} />
          <Route path="estate/*" element={<EstateSection caseData={caseData} />} />
          <Route path="drafts/*" element={<DraftsSection caseData={caseData} />} />
          <Route path="assistant/*" element={<AssistantSection caseData={caseData} />} />

          {/* Legacy redirects from the old flat tab structure */}
          <Route path="upload" element={<Navigate to="../discovery/upload" replace />} />
          <Route path="accounts" element={<Navigate to="../investigation/accounts" replace />} />
          <Route path="transactions" element={<Navigate to="../investigation/transactions" replace />} />
          <Route path="cash-flow" element={<Navigate to="../investigation/cash-flow" replace />} />
          <Route path="assets" element={<Navigate to="../estate/assets" replace />} />
          <Route path="net-value-split" element={<Navigate to="../estate/split" replace />} />
          <Route path="demand-letters" element={<Navigate to="../drafts/letters" replace />} />
          <Route path="inventory" element={<Navigate to="../drafts/inventory" replace />} />
          <Route path="reports" element={<Navigate to="../drafts/reports" replace />} />

          <Route path="*" element={<Navigate to="discovery/upload" replace />} />
        </Routes>
      </div>
    </div>
  );
}
